import React, { useState } from "react";
import { entities, getDisplayMessage, openUploadInNewTab } from "@/api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  FileText,
  Plus,
  Eye,
  Edit2,
  Trash2,
  Download,
  Search
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const statusConfig = {
  rascunho: {
    label: "Rascunho",
    color: "bg-gray-100 text-gray-700 border-gray-200"
  },
  finalizada: {
    label: "Finalizada",
    color: "bg-blue-100 text-blue-700 border-blue-200"
  },
  enviada: {
    label: "Enviada",
    color: "bg-green-100 text-green-700 border-green-200"
  }
};

export default function PropostasPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busca, setBusca] = useState("");
  const [propostaExcluir, setPropostaExcluir] = useState(null);

  const { data: propostas = [], isLoading } = useQuery({
    queryKey: ['propostas'],
    queryFn: () => entities.Proposta.list('-createdAt')
  });

  const { data: editais = [] } = useQuery({
    queryKey: ['editais-propostas'],
    queryFn: () => entities.Edital.list()
  });

  const deletePropostaMutation = useMutation({
    mutationFn: (id) => entities.Proposta.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['propostas'] });
      setPropostaExcluir(null);
    },
    onError: (error) => toast.error(getDisplayMessage(error, "Erro ao excluir proposta. Tente novamente.")),
  });

  const propostasFiltradas = (Array.isArray(propostas) ? propostas : []).filter(p => {
    if (!busca) return true;
    const edital = (Array.isArray(editais) ? editais : []).find(e => e.id === (p.editalId ?? p.edital_id));
    const searchText = busca.toLowerCase();
    return (
      (p.numeroProposta ?? p.numero_proposta)?.toLowerCase().includes(searchText) ||
      (p.aoInstituicao ?? p.ao_instituicao)?.toLowerCase().includes(searchText) ||
      (p.pregaoNumero ?? p.pregao_numero)?.toLowerCase().includes(searchText) ||
      edital?.titulo_licitacao?.toLowerCase().includes(searchText)
    );
  });

  const getEdital = (editalId) => {
    return (Array.isArray(editais) ? editais : []).find(e => e.id === editalId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 md:p-8" data-tour="tour-Propostas-root">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900">Propostas de Preço</h1>
            <p className="text-slate-600 mt-1">Gerencie suas propostas comerciais</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate(createPageUrl("CriarProposta"))}
              className="bg-blue-600 hover:bg-blue-700"
              data-tour="tour-Propostas-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Proposta
            </Button>
          </div>
        </div>

        <Card className="border-none shadow-xl bg-white/90 backdrop-blur-sm mb-6">
          <CardContent className="p-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                placeholder="Buscar por número, instituição, pregão..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4">
          {isLoading ? (
            Array(3).fill(0).map((_, i) => (
              <Card key={i} className="border-none shadow-md animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                </CardContent>
              </Card>
            ))
          ) : propostasFiltradas.length === 0 ? (
            <Card className="border-none shadow-md">
              <CardContent className="p-12 text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500 text-lg mb-4">
                  {busca ? 'Nenhuma proposta encontrada' : 'Nenhuma proposta criada ainda'}
                </p>
                {!busca && (
                  <Button onClick={() => navigate(createPageUrl("CriarProposta"))}>
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Primeira Proposta
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            propostasFiltradas.map((proposta) => {
              const editalId = proposta.editalId ?? proposta.edital_id;
              const edital = getEdital(editalId);
              const instituicao = proposta.ao_instituicao ?? proposta.aoInstituicao;
              const dataProposta = proposta.data_proposta ?? proposta.dataProposta;
              const valorTotal = proposta.valor_total_proposta ?? proposta.valorTotalProposta;
              
              return (
                <Card key={proposta.id} className="border-none shadow-md hover:shadow-xl transition-all duration-300 bg-white/90 backdrop-blur-sm">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row gap-6">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-4">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-lg text-slate-900 mb-1">
                              {proposta.descricao || (() => {
                                const idPart = `Proposta ${proposta.numero_proposta || `#${proposta.id?.slice(0, 8) || ''}`}`;
                                const titulo = edital && (edital.titulo_licitacao || edital.tituloLicitacao || edital.instituicao || edital.nome_arquivo || edital.nomeArquivo);
                                return titulo ? `${idPart} - ${titulo}` : idPart;
                              })()}
                            </h3>
                          </div>
                        </div>

                        <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
                          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
                            <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Instituição
                            </span>
                            <span className="text-sm font-medium text-slate-900">
                              {instituicao || "Não informado"}
                            </span>
                          </div>
                          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
                            <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Data da proposta
                            </span>
                            <span className="text-sm font-medium text-slate-900">
                              {dataProposta ? formatDate(dataProposta) : "Não informado"}
                            </span>
                          </div>
                          <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
                            <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
                              Valor total da proposta
                            </span>
                            <span className="text-sm font-semibold text-slate-900">
                              {formatCurrency(valorTotal || 0)}
                            </span>
                          </div>
                        </div>

                        {proposta.pregao_numero && (
                          <div className="mb-2">
                            <span className="text-sm text-slate-600">Pregão: </span>
                            <span className="text-sm font-medium text-slate-900">
                              {proposta.pregao_numero}
                            </span>
                          </div>
                        )}

                        {edital && (
                          <div className="mb-3">
                            <span className="text-sm text-slate-600">Edital: </span>
                            <span className="text-sm font-medium text-slate-900">
                              {edital.titulo_licitacao || edital.instituicao}
                            </span>
                          </div>
                        )}

                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline" className={`${statusConfig[proposta.status]?.color || statusConfig.rascunho.color} border`}>
                            {statusConfig[proposta.status]?.label || 'Rascunho'}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex flex-col gap-2 lg:w-48">
                        <Button
                          variant="outline"
                          onClick={() => navigate(createPageUrl("VisualizarProposta") + `?id=${proposta.id}`)}
                          className="w-full"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Visualizar
                        </Button>

                        <Button
                          variant="outline"
                          onClick={() => navigate(createPageUrl("CriarProposta") + `?id=${proposta.id}`)}
                          className="w-full"
                        >
                          <Edit2 className="w-4 h-4 mr-2" />
                          Editar
                        </Button>

                        {proposta.pdf_url && (
                          <Button
                            variant="outline"
                            onClick={() => openUploadInNewTab(proposta.pdf_url)}
                            className="w-full border-green-300 text-green-700 hover:bg-green-50"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Baixar PDF
                          </Button>
                        )}

                        <Button
                          variant="outline"
                          onClick={() => setPropostaExcluir(proposta)}
                          className="w-full border-red-300 text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Excluir
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>

      <AlertDialog open={!!propostaExcluir} onOpenChange={(open) => !open && setPropostaExcluir(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta proposta? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePropostaMutation.isPending}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletePropostaMutation.mutate(propostaExcluir.id)}
              disabled={deletePropostaMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletePropostaMutation.isPending ? 'Excluindo...' : 'Excluir'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}